import {
  Building2,
  HandCoins,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { formatCurrency } from '../../../shared/utils';
import type { FinancialSummary } from '../../../services/financial/types';

export type FinancialSummaryCardKey = keyof FinancialSummary;

type FinancialSummaryCardsProps = {
  summary?: FinancialSummary | null;
  isLoading?: boolean;
  /** Cards exibidos (default: todos). Quem decide é o perfil — ex.: Corretor só vê a própria comissão. */
  visibleCards?: FinancialSummaryCardKey[];
};

const CARD_BASE_CLASS =
  'flex flex-col gap-1 rounded-2xl border border-alba-gray-200 bg-white px-3 py-3 lg:px-4';
const ICON_WRAP_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alba-green-50 lg:h-9 lg:w-9';
const LABEL_CLASS =
  'font-montserrat text-xs leading-tight text-alba-gray-600 lg:text-sm';
const VALUE_CLASS =
  'w-full truncate text-right font-montserrat text-lg font-bold leading-none text-alba-black-500 tabular-nums lg:text-2xl';

type CardConfig = {
  key: FinancialSummaryCardKey;
  label: string;
  icon: LucideIcon;
};

const CARDS: CardConfig[] = [
  { key: 'totalValue', label: 'Valor Total', icon: HandCoins },
  { key: 'advisoryCommission', label: 'Comissão Assessoria', icon: Building2 },
  { key: 'brokerCommission', label: 'Comissão Corretor', icon: UsersRound },
];

export function FinancialSummaryCards({
  summary,
  isLoading,
  visibleCards,
}: FinancialSummaryCardsProps) {
  const cards = visibleCards
    ? CARDS.filter(card => visibleCards.includes(card.key))
    : CARDS;

  return (
    <div className='flex flex-col gap-3'>
      <span className='font-montserrat text-sm text-alba-gray-600'>
        Próximo Pagamento
      </span>
      <div className='grid w-full grid-cols-2 gap-3 sm:grid-cols-3 lg:max-w-4xl lg:gap-4'>
        {cards.map(({ key, label, icon: Icon }) => {
          const value = summary?.[key];
          const display =
            isLoading || value == null ? '—' : formatCurrency(value);
          return (
            <div key={key} className={CARD_BASE_CLASS}>
              <div className='flex items-center justify-between gap-2'>
                <div className={ICON_WRAP_CLASS}>
                  <Icon
                    className='h-4 w-4 text-alba-green-600 lg:h-5 lg:w-5'
                    strokeWidth={1.6}
                  />
                </div>
                <div className='flex min-w-0 flex-1 items-center justify-end'>
                  <span
                    title={value != null ? formatCurrency(value) : undefined}
                    className={VALUE_CLASS}
                  >
                    {display}
                  </span>
                </div>
              </div>
              <span className={LABEL_CLASS}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
